import { Injectable } from '@angular/core';

import { Subject } from 'rxjs';
import { notify } from './notify';

@Injectable({
  providedIn: 'root',
})
export class GpsService {
  #watchId?: number;
  #lastLatitude?: number;
  #lastLongitude?: number;
  #lastUpdateTime?: number;

  // Add this private field above your methods
  #position$?: Subject<GeolocationPosition | null>;

  startWatching(): Subject<GeolocationPosition | null> {
    if (this.#watchId && this.#position$) {
      return this.#position$;
    }

    this.#position$ = new Subject<GeolocationPosition | null>();
    this.#watchId = navigator.geolocation.watchPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;
        const currentTime = Date.now();

        // Check if 10 seconds have passed since last update
        const hasTimeoutPassed =
          !this.#lastUpdateTime || currentTime - this.#lastUpdateTime >= 10000;

        const hasPositionChanged =
          lat !== this.#lastLatitude || lon !== this.#lastLongitude;

        if (hasPositionChanged && hasTimeoutPassed) {
          this.#lastLatitude = lat;
          this.#lastLongitude = lon;
          this.#lastUpdateTime = currentTime;
          this.#position$!.next(position);
        }
      },
      (error) => {
        this.handleError(error);
        this.#position$!.next(null);
      },
      {
        enableHighAccuracy: true,
      },
    );

    return this.#position$;
  }

  private async handleError(error: GeolocationPositionError) {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        notify('Permesso GPS negato', 5000, 'error');
        break;
      case error.POSITION_UNAVAILABLE:
        notify('Posizione non disponibile', 3000, 'warning');
        this.startWatching();
        break;
      case error.TIMEOUT:
        notify('Timeout GPS riprovo', 3000, 'warning');
        this.startWatching();
        break;
      default:
        notify('Errore GPS sconosciuto', 3000, 'error');
        console.error('Errore GPS:', error.message);
    }
  }

  getCurrentPosition(): Promise<GeolocationPosition | null> {
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          notify('GPS attivato Posizione acquisita', 1000, 'success');
          resolve(position);
        },
        (error) => {
          this.handleError(error);
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 60000, // Accept cached position up to 1 minute old
        },
      );
    });
  }

  stopWatching() {
    if (this.#watchId) {
      navigator.geolocation.clearWatch(this.#watchId);
    }
    this.#watchId = undefined;
    this.#lastUpdateTime = undefined;

    // Complete and reset the position subject
    if (this.#position$) {
      this.#position$.complete();
      this.#position$ = undefined;
    }
  }

  isWatching(): boolean {
    return !!this.#watchId;
  }

  getLastKnownPosition(): {
    latitude?: number;
    longitude?: number;
    timestamp?: number;
  } | null {
    if (this.#lastLatitude !== undefined && this.#lastLongitude !== undefined) {
      return {
        latitude: this.#lastLatitude,
        longitude: this.#lastLongitude,
        timestamp: this.#lastUpdateTime,
      };
    }
    return null;
  }
}
